import config from 'constants/config'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import {
  isBookingProtectionItem,
  isCruiseItem,
  isInsuranceItem,
  isLuxPlusSubscriptionItem,
  isSubscriptionJoinItem,
  isTourV2Item,
} from 'lib/checkout/checkoutUtils'

function isPassportCardEnabledVertical(itemType: string): boolean {
  return isEnabledForFeature(config.PASSPORT_CARD_ENABLED_ITEM_TYPES, itemType)
}

// Non-primary items that may sit alongside the tour/cruise without changing the
// "standalone tours/cruises" nature of the cart (e.g. the insurance item itself
// once selected, or a LuxPlus subscription).
function isAllowedAddonItem(item: App.Checkout.AnyItem): boolean {
  return isInsuranceItem(item) ||
    isBookingProtectionItem(item) ||
    isLuxPlusSubscriptionItem(item) ||
    isSubscriptionJoinItem(item)
}

// PassportCard cannot price a package that carries a tour DP and a cruise DP
// together — their proposals API answers responseCode 050 ("No proposal available
// for the request") with zero proposals, while the same items quote fine on their
// own. Such carts keep XCover rather than reaching a PassportCard section with
// nothing to show.
function hasBothTourAndCruise(items: Array<App.Checkout.AnyItem>): boolean {
  return items.some(isTourV2Item) && items.some(isCruiseItem)
}

/**
 * True when the cart contains only PassportCard-enabled verticals (tours and/or
 * cruises, per PASSPORT_CARD_ENABLED_ITEM_TYPES) plus insurance / subscription
 * addons, and at least one such primary item. Tour+hotel and tour+experience
 * carts are not standalone, and tour+cruise carts cannot be priced, so all of
 * them keep XCover.
 *
 * Leaf helper (config + item guards only) so it can be reused by both the
 * section-gating selector and the quote-request selector without creating an
 * import cycle through the insurance selector graph.
 */
export function isStandaloneToursCruisesCart(items: Array<App.Checkout.AnyItem>): boolean {
  const hasEnabledVertical = items.some(item => isPassportCardEnabledVertical(item.itemType))
  if (!hasEnabledVertical) return false
  if (hasBothTourAndCruise(items)) return false

  return items.every(item => isPassportCardEnabledVertical(item.itemType) || isAllowedAddonItem(item))
}

/**
 * Adults across the tour/cruise items in the cart, which for a standalone
 * tours/cruises cart is the number of adults the policy would cover - the same total
 * getCheckoutInsuranceTravellers puts in the quote payload. Summed, not maxed: two
 * items of two adults is four on the policy, and reading only the largest item would
 * let the cart past the adult cap and have svc-insurance reject the quote instead.
 *
 * Read straight off item occupancy rather than through the insurance-occupancy
 * selectors, which cannot be imported here - the resulting cycle breaks
 * checkoutSectionsSelectors at module-init ("getInsuranceEnabledCheckoutItems is not a
 * function").
 */
export function getToursCruisesAdultCount(items: Array<App.Checkout.AnyItem>): number {
  return items.reduce((totalAdults, item) => {
    const adults = (isTourV2Item(item) || isCruiseItem(item)) ? (item.occupancy?.adults ?? 0) : 0
    return totalAdults + adults
  }, 0)
}
